// Ring 0 — pure poker evaluator wrapper.
//
// This is the SOLE consumer of `pokersolver` in the codebase (D-01). All
// other modules (Cashier, RunUI, debug seam) call evaluateHand / bestOfPocket
// — never `Hand.solve` directly. That keeps the Plan-B hot-swap to
// `@poker-apprentice/hand-evaluator` mechanical (CONTEXT) if pokersolver
// fails any corpus case.
//
// NO Phaser imports. NO Math.random. (CLAUDE.md anti-patterns.)
//
// pokersolver card encoding (RESEARCH Pattern 2):
//   ranks: A K Q J T 9 8 7 6 5 4 3 2  (10 = T)
//   suits: d h c s  (lowercase)
// Our Card<{rank,suit}> shape uses the same single-letter codes, so
// `${rank}${suit}` joins straight to pokersolver input.

import { Hand } from 'pokersolver';
import type { Card, HandResult } from '../run/types';

const cardToCode = (c: Card): string => `${c.rank}${c.suit}`;

/**
 * Evaluate any 5+ cards as a poker hand. pokersolver internally selects the
 * best 5 from 6 or 7 cards (RESEARCH A1 — verified by handEval.test.ts).
 *
 * Throws on fewer than 5 cards. Callers that want null-on-too-few should use
 * `bestOfPocket` instead.
 */
export function evaluateHand(cards: Card[]): HandResult {
  if (cards.length < 5) {
    throw new Error(`evaluateHand requires >=5 cards, got ${cards.length}`);
  }
  const solved = Hand.solve(cards.map(cardToCode));
  return {
    name: solved.name,
    rank: solved.rank,
    descr: solved.descr,
    cards: solved.cards.map((sc) => ({
      rank: sc.value as Card['rank'],
      suit: sc.suit as Card['suit'],
    })),
  };
}

/**
 * Best 5-card hand from a player's pocket (6 or 7 cards typical). Returns
 * null when the pocket has fewer than 5 cards — call site (HUD "best hand
 * so far") can render "—" without try/catch.
 *
 * Delegates to evaluateHand once the size guard passes; pokersolver's
 * built-in best-5 selection handles the 6/7-card case (RESEARCH A1).
 */
export function bestOfPocket(pocket: Card[]): HandResult | null {
  if (pocket.length < 5) return null;
  return evaluateHand(pocket);
}
